import React, { useEffect, useState } from "react";
import { Nav, NavItem, NavLink, Spinner } from "reactstrap";
import { getPublicResearchStats } from "../../services/userPredictionService";
import {
  rateChart, calibrationChart, rateAnswer, confidenceAnswer, THIN_RECORD,
} from "./researchHubHtml";
import "./ResearchHub.css";

/**
 * Charts for whatever the filters on /research currently select.
 *
 * The hub above the listing describes the whole public record and disappears
 * once a filter is set. These tabs answer the same questions for the filtered
 * set instead: the server counts exactly the rows the table lists
 * (/public/stats shares the listing's WHERE) and grades them with the hub's own
 * compute, and the charts and short answers are the hub's functions -- so a
 * figure here and the same figure on /research read the same way.
 *
 * Every short answer is written from the figures, states its numerator and
 * denominator, and leaves records under THIN_RECORD graded calls out of any
 * comparison, as the hub does.
 */

export const VIEWS = [
  { id: "list", label: "Forecasts", icon: "ri-list-check" },
  { id: "models", label: "By model", icon: "ri-bar-chart-horizontal-line" },
  { id: "symbols", label: "Model × symbol", icon: "ri-grid-line" },
  { id: "horizons", label: "By horizon", icon: "ri-time-line" },
  { id: "confidence", label: "Confidence", icon: "ri-focus-3-line" },
];

const HORIZON = { "1h": "1 hour", "4h": "4 hours", "1d": "1 day", "2d": "2 days", "7d": "1 week", "30d": "1 month", "180d": "6 months" };
const pct = (v) => (v === null || v === undefined ? "—" : `${Math.round(v * 100)}%`);
const ofCount = (v, hit, total) => (total ? `${pct(v)} (${hit} of ${total})` : "—");
// Enough symbols to read at a glance; the rest are named, not drawn.
const MAX_SYMBOL_CHARTS = 12;

export const ViewTabs = ({ view, onChange }) => (
  <Nav tabs className="nav-tabs-custom mb-3">
    {VIEWS.map((v) => (
      <NavItem key={v.id}>
        <NavLink href="#" active={view === v.id}
                 onClick={(e) => { e.preventDefault(); onChange(v.id); }}>
          <i className={`${v.icon} align-bottom me-1`} />{v.label}
        </NavLink>
      </NavItem>
    ))}
  </Nav>
);

const Chart = ({ svg, title, howToRead }) => (svg ? (
  <>
    <h3 className="hub-chart-title">{title}</h3>
    <figure className="hub-figure">
      {/* The hub's chart builders escape everything they interpolate. */}
      <div dangerouslySetInnerHTML={{ __html: svg }} />
      {howToRead && <figcaption><strong>How to read it.</strong> {howToRead}</figcaption>}
    </figure>
  </>
) : null);

const Answer = ({ children }) => (
  <p className="hub-answer"><strong>What it means.</strong> {children}</p>
);

const RATE_HOW_TO_READ = `Each bar is the share of directional calls that were right, with the calls behind it in brackets. The dashed line is 50%, a coin flip. Pale bars rest on fewer than ${THIN_RECORD} graded calls and are left out of the comparison above.`;

// The whole filtered set in one sentence: how much of it has an outcome yet.
function scopeSentence(t, filterLabel) {
  const subject = filterLabel ? `These filters (${filterLabel})` : "The public record";
  const verb = filterLabel ? "match" : "has";
  if (!t.forecasts) {
    return `${subject} ${verb} no forecast counted from the evaluation baseline${t.beforeBaseline ? ` — ${t.beforeBaseline} listed below predate it and are not scored` : ""}.`;
  }
  const parts = [
    `${subject} ${verb} ${t.forecasts} counted forecast${t.forecasts === 1 ? "" : "s"}`,
    `${t.settled} settled`,
  ];
  if (t.open + t.awaitingSettlement) parts.push(`${t.open + t.awaitingSettlement} not yet settled`);
  let s = `${parts.join(", ")}. `;
  if (t.graded) {
    s += `Of ${t.graded} directional call${t.graded === 1 ? "" : "s"} with an outcome, ${ofCount(t.accuracy, t.right, t.graded)} were right`;
    s += t.noCall ? `; ${t.noCall} forecast${t.noCall === 1 ? " was" : "s were"} too even to count as a call.` : ".";
  } else if (t.settled) {
    s += "None of the settled forecasts made a directional call, so there is no direction record yet.";
  }
  if (t.rangeChecked) s += ` The close landed inside the forecast range ${ofCount(t.rangeCoverage, t.rangeHits, t.rangeChecked)}.`;
  if (t.graded && t.graded < THIN_RECORD) s += ` With fewer than ${THIN_RECORD} graded calls this is an insufficient sample — read it as a record, not a finding.`;
  if (t.beforeBaseline) s += ` ${t.beforeBaseline} older forecast${t.beforeBaseline === 1 ? "" : "s"} in the list predate the evaluation baseline and are not counted.`;
  if (t.lookAhead) s += ` ${t.lookAhead} stock and ETF forecast${t.lookAhead === 1 ? " was" : "s were"} made outside market hours against the previous close, after news that close did not reflect, and ${t.lookAhead === 1 ? "is" : "are"} not counted either.`;
  return s;
}

// Per-author benches are never pooled -- the hub's rule, kept here.
const benchNote = (stats) => ((stats.benches || []).length > 1
  ? " Each author's models are shown separately and never pooled: two members running the same model are not running the same experiment."
  : "");

function ModelsView({ stats }) {
  return (stats.benches || []).map((b) => (
    <div key={b.author} className="hub-bench">
      <Answer>{rateAnswer(b.models || [], "model")}{benchNote(stats)}</Answer>
      <Chart title={`Direction right, by model — ${b.author}`}
             svg={rateChart(b.models || [], { id: `fm-${b.author}`, title: `Direction right, by model — ${b.author}` })}
             howToRead={RATE_HOW_TO_READ} />
      <ModelTable rows={b.models || []} />
    </div>
  ));
}

function ModelTable({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="hub-table">
      <table>
        <thead>
          <tr><th>Model</th><th>Forecasts</th><th>Settled</th><th>Direction right</th><th>Close in range</th><th>Avg score</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id || r.name}>
              <th scope="row">{r.name}</th>
              <td>{r.forecasts}</td>
              <td>{r.settled}</td>
              <td>{ofCount(r.accuracy, r.right, r.graded)}</td>
              <td>{ofCount(r.rangeCoverage, r.rangeHits, r.rangeChecked)}</td>
              <td>{r.averageScore == null ? "—" : Number(r.averageScore).toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SymbolsView({ stats }) {
  const symbols = (stats.assets || []).map((a) => a.symbol);
  const shown = symbols.slice(0, MAX_SYMBOL_CHARTS);
  const rest = symbols.slice(MAX_SYMBOL_CHARTS);
  return (
    <>
      <Answer>
        {symbols.length === 1
          ? `All of these forecasts are on ${symbols[0]}, so this is the model comparison for that one asset.`
          : `${symbols.length} assets are in this selection. Each chart compares the models on one asset, so a model whose record rests on a single ticker shows up as strong on one chart and absent or weak on the others.`}
        {benchNote(stats)}
      </Answer>
      {shown.map((symbol) => (stats.benches || []).map((b) => {
        const rows = (b.models || [])
          .map((m) => ({ ...(m.symbols || []).find((s) => s.symbol === symbol), name: m.name }))
          .filter((r) => r.forecasts);
        if (!rows.length) return null;
        const title = `${symbol} — direction right, by model${(stats.benches || []).length > 1 ? ` (${b.author})` : ""}`;
        const svg = rateChart(rows, { id: `fs-${symbol}-${b.author}`, title });
        const graded = rows.reduce((n, r) => n + r.graded, 0);
        return (
          <div key={`${symbol}-${b.author}`} className="hub-bench">
            {svg ? (
              <>
                <Chart title={title} svg={svg} />
                <p className="fs-13"><strong>{symbol}:</strong> {rateAnswer(rows, "model")}</p>
              </>
            ) : (
              <p className="fs-13 mt-3">
                <strong>{symbol}:</strong> {rows.reduce((n, r) => n + r.forecasts, 0)} forecast(s) from {rows.length} model(s),
                {graded ? "" : " none with a graded directional call yet"}.
              </p>
            )}
          </div>
        );
      }))}
      {rest.length > 0 && (
        <p className="text-muted fs-13">Not charted, with smaller records: {rest.join(", ")}. Filter on a symbol to see its chart.</p>
      )}
      <p className="text-muted fs-12 mt-3">{RATE_HOW_TO_READ}</p>
    </>
  );
}

function HorizonsView({ stats }) {
  const rows = (stats.horizons || []).map((h) => ({ ...h, name: HORIZON[h.horizon] || h.horizon }));
  return (
    <>
      <Answer>
        {rows.length === 1
          ? `Every forecast here is over ${rows[0].name}: right on direction ${ofCount(rows[0].accuracy, rows[0].right, rows[0].graded)}. Remove the horizon filter to compare horizons.`
          : `${rateAnswer(rows, "horizon")} Horizons are pooled across models: this asks whether a longer window is easier to call, not which model is best.`}
      </Answer>
      <Chart title="Direction right, by horizon"
             svg={rateChart(rows, { id: "fh", title: "Direction right, by horizon" })}
             howToRead={RATE_HOW_TO_READ} />
    </>
  );
}

function ConfidenceView({ stats }) {
  return (
    <>
      <Answer>Does a higher stated confidence mean a better forecast here? {confidenceAnswer(stats.calibration)}</Answer>
      <Chart title="Stated confidence against outcome"
             svg={calibrationChart(stats.calibration, { id: "fc", title: "Stated confidence against outcome" })}
             howToRead={`Across the bottom, the confidence the model stated; up the side, how often it came good. Blue points are closes inside the range, orange points are directions right. Larger points rest on more forecasts; pale ones on fewer than ${THIN_RECORD}. On the dashed line, stated confidence matches the record: below it, the model claimed more than it delivered.`} />
    </>
  );
}

const FilteredStats = ({ view, params, filterLabel }) => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const key = JSON.stringify(params);

  useEffect(() => {
    let live = true;
    setStats(null);
    setError(null);
    getPublicResearchStats(params)
      .then((res) => { if (live) setStats(res.data || null); })
      .catch((e) => { if (live) setError((typeof e === "string" && e) || e?.message || "Could not load the statistics."); });
    return () => { live = false; };
    // params is rebuilt every render; key is its content.
  }, [key]); // eslint-disable-line

  if (error) return <p className="text-muted text-center py-4">{error}</p>;
  if (!stats) return <div className="text-center py-4"><Spinner /></div>;

  const t = stats.totals || {};
  return (
    <div className="research-hub" style={{ maxWidth: "none" }}>
      <p className="hub-shows">{scopeSentence(t, filterLabel)}</p>
      {!t.forecasts ? null : (
        <>
          {view === "models" && <ModelsView stats={stats} />}
          {view === "symbols" && <SymbolsView stats={stats} />}
          {view === "horizons" && <HorizonsView stats={stats} />}
          {view === "confidence" && <ConfidenceView stats={stats} />}
        </>
      )}
      <p className="text-muted fs-12 mt-3 mb-0">
        Graded by the same rule as every forecast page — <a href="/how-scoring-works">how scoring works</a>.
        Every figure comes from the forecasts listed under “Forecasts”.
      </p>
    </div>
  );
};

export default FilteredStats;
